import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Alert, Table, Form } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { toast } from 'react-toastify';
import { getAllCompanies } from '../../api/companyApi';
import { submitPreferences, getUserPreferences } from '../../api/preferencesApi';

const PreferencesSubmission = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [submittedPreferences, setSubmittedPreferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Check if preferences are already submitted
        const prefs = await getUserPreferences();
        if (prefs.preferences && prefs.preferences.length > 0) {
          setSubmittedPreferences(prefs.preferences);
        } else {
          // Fetch companies if no preferences exist
          const data = await getAllCompanies();
          setCompanies(data || []);
          setAvailableCompanies(data || []);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to fetch data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSelectCompany = (event) => {
    const companyId = parseInt(event.target.value);
    if (companyId) {
      const company = availableCompanies.find((c) => c.id === companyId);
      if (company) {
        setSelectedCompanies([...selectedCompanies, { ...company, preference_order: selectedCompanies.length + 1 }]);
        setAvailableCompanies(availableCompanies.filter((c) => c.id !== companyId));
      }
    }
  };

  const handleRemoveCompany = (companyId) => {
    const company = selectedCompanies.find((c) => c.id === companyId);
    if (company) {
      setAvailableCompanies([...availableCompanies, { ...company }]);
      const newSelected = selectedCompanies.filter((c) => c.id !== companyId).map((c, idx) => ({
        ...c,
        preference_order: idx + 1,
      }));
      setSelectedCompanies(newSelected);
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(selectedCompanies);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    const updatedItems = items.map((item, index) => ({
      ...item,
      preference_order: index + 1,
    }));
    setSelectedCompanies(updatedItems);
  };

  const handleSubmit = async () => {
    if (selectedCompanies.length === 0) {
      setError('Please select at least one company.');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      const preferencesData = selectedCompanies.map((company) => ({
        company_id: company.id,
        preference_order: company.preference_order,
      }));
      await submitPreferences(preferencesData);
      toast.success('Preferences submitted successfully!');
      // Update state to show submitted preferences
      setSubmittedPreferences(preferencesData.map((pref, idx) => ({
        ...selectedCompanies[idx],
        company_id: pref.company_id,
        preference_order: pref.preference_order,
      })));
      setSelectedCompanies([]);
      setAvailableCompanies([]);
    } catch (error) {
      console.error('Error submitting preferences:', error);
      setError(error.message || 'Failed to submit preferences. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading...</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <h1 className="mb-4">Company Preferences</h1>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="mb-4">
        <Card.Header as="h5">
          {submittedPreferences.length > 0 ? 'Your Submitted Preferences' : 'Submit Your Preferences'}
        </Card.Header>
        <Card.Body>
              {submittedPreferences.length > 0 ? (
        <>
          <Alert variant="success">Your preferences have been submitted and cannot be changed.</Alert>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th width="80">Preference</th>
                <th>Company</th>
                <th width="120">Visit Date</th>
              </tr>
            </thead>
            <tbody>
              {submittedPreferences
                .slice() // Create a copy to avoid mutating the original array
                .sort((a, b) => a.preference_order - b.preference_order) // Sort by preference_order
                .map((pref) => (
                  <tr key={pref.company_id}>
                    <td className="text-center">{pref.preference_order}</td>
                    <td>{pref.name}</td>
                    <td>{new Date(pref.visit_date).toLocaleDateString()}</td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </>
          ) : (
            <>
              <Form.Group className="mb-4">
                <Form.Label>Add a company to your preferences</Form.Label>
                <Form.Select onChange={handleSelectCompany}>
                  <option value="">Select a company</option>
                  {availableCompanies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name} - {new Date(company.visit_date).toLocaleDateString()}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <div className="mb-4">
                <h5>Your Preferences</h5>
                <p className="text-muted small">Drag and drop to reorder your preferences</p>

                {selectedCompanies.length === 0 ? (
                  <Alert variant="info">
                    You haven't selected any companies yet. Use the dropdown above to add companies.
                  </Alert>
                ) : (
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="preferences">
                      {(provided) => (
                        <Table striped bordered hover ref={provided.innerRef} {...provided.droppableProps}>
                          <thead>
                            <tr>
                              <th width="80">Preference</th>
                              <th>Company</th>
                              <th width="120">Visit Date</th>
                              <th width="100">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedCompanies.map((company, index) => (
                              <Draggable key={company.id} draggableId={company.id.toString()} index={index}>
                                {(provided) => (
                                  <tr
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                  >
                                    <td className="text-center">{company.preference_order}</td>
                                    <td>{company.name}</td>
                                    <td>{new Date(company.visit_date).toLocaleDateString()}</td>
                                    <td>
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => handleRemoveCompany(company.id)}
                                      >
                                        Remove
                                      </Button>
                                    </td>
                                  </tr>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </tbody>
                        </Table>
                      )}
                    </Droppable>
                  </DragDropContext>
                )}
              </div>

              <div className="d-grid">
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={selectedCompanies.length === 0 || submitting}
                >
                  {submitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Submitting...
                    </>
                  ) : (
                    'Submit Preferences'
                  )}
                </Button>
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default PreferencesSubmission;